import JSON5 from 'json5';

export interface CustomQuestionChoice {
  value: string;
  text: string;
}

export interface CustomQuestionElement {
  type: string;
  name: string;
  title?: string;
  description?: string;
  isRequired?: boolean;
  inputType?: string;
  visibleIf?: string;
  maxLength?: number;
  choices?: CustomQuestionChoice[];
}

export interface CustomQuestionsSchema {
  title?: string;
  description?: string;
  pages?: { name?: string; elements?: CustomQuestionElement[] }[];
}

export interface MappedCustomQuestion {
  name: string;
  title: string;
  description?: string;
  type: string;
  response: string;
}

const tryParse = <T>(value: unknown): T | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'object') return value as T;
  if (typeof value !== 'string') return null;
  try {
    return JSON5.parse(value) as T;
  } catch {
    return null;
  }
};

const flattenElements = (
  schema: CustomQuestionsSchema | null,
): CustomQuestionElement[] => {
  if (!schema?.pages?.length) return [];
  return schema.pages.flatMap(p => p.elements || []);
};

const formatChoiceValues = (
  rawValues: unknown,
  choices: CustomQuestionChoice[],
): string => {
  const values = Array.isArray(rawValues) ? rawValues : [rawValues];
  return values
    .map(v => choices.find(c => c.value === v)?.text || String(v))
    .join(', ');
};

const formatResponse = (
  raw: unknown,
  element: CustomQuestionElement | undefined,
  notAnsweredText: string,
): string => {
  if (raw === null || raw === undefined || raw === '') return notAnsweredText;

  if (element?.choices?.length) {
    return formatChoiceValues(raw, element.choices);
  }

  if (Array.isArray(raw)) {
    return raw.length ? raw.map(String).join(', ') : notAnsweredText;
  }

  if (typeof raw === 'object') {
    try {
      return JSON.stringify(raw);
    } catch {
      return notAnsweredText;
    }
  }

  return String(raw);
};

/**
 * Parse the legacy `customQuestions` survey schema (JSON / JSON5 string) and
 * the `customQuestionResponses` (JSON / JSON5 string keyed by question `name`)
 * and return a flat list of question + response pairs enriched with each
 * element's metadata (title, description, type), with checkbox values mapped
 * back to their human-readable `text` via the element's `choices`.
 */
export interface MapCustomQuestionResponsesOptions {
  /** When true, omit questions that have no answer. */
  hideUnanswered: boolean;
  /** Text to display for unanswered questions. */
  notAnsweredText: string;
}

const isAnswered = (raw: unknown): boolean => {
  if (raw === null || raw === undefined || raw === '') return false;
  if (Array.isArray(raw) && raw.length === 0) return false;
  return true;
};

export const mapCustomQuestionResponses = (
  customQuestions: unknown,
  customQuestionResponses: unknown,
  options: MapCustomQuestionResponsesOptions,
): MappedCustomQuestion[] => {
  const { hideUnanswered, notAnsweredText } = options;
  const schema = tryParse<CustomQuestionsSchema>(customQuestions);
  const responses =
    tryParse<Record<string, unknown>>(customQuestionResponses) || {};

  const elements = flattenElements(schema);
  const seen = new Set<string>();
  const result: MappedCustomQuestion[] = [];

  // 1. Walk the schema in declared order so unanswered questions still appear.
  for (const element of elements) {
    seen.add(element.name);
    const hasResponse = Object.prototype.hasOwnProperty.call(
      responses,
      element.name,
    );
    const rawValue = hasResponse ? responses[element.name] : undefined;
    const answered = hasResponse && isAnswered(rawValue);

    if (hideUnanswered && !answered) continue;

    result.push({
      name: element.name,
      title: element.title || element.name,
      description: element.description,
      type: element.type || 'text',
      response: answered
        ? formatResponse(rawValue, element, notAnsweredText)
        : notAnsweredText,
    });
  }

  // 2. Append any responses that don't have a matching schema element.
  for (const [name, value] of Object.entries(responses)) {
    if (seen.has(name)) continue;
    if (hideUnanswered && !isAnswered(value)) continue;
    result.push({
      name,
      title: name,
      type: 'text',
      response: formatResponse(value, undefined, notAnsweredText),
    });
  }

  return result;
};
